import './StreamingOptimizations.scss';
import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd, MdContentCopy, MdDelete, MdPlayArrow } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Button } from '@Components/Button';
import { ConstraintFormModal } from '@Components/ConstraintFormModal';
import { DatePicker } from '@Components/DatePicker';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size
} from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { formatOptimizationStatus } from '@Components/OptimizationFailureDetails/constraint-diagnostics-utils';
import { Page } from '@Components/Page/Page';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { WidgetContainer } from '@Components/WidgetContainer';
import { OptimizationRun } from './PostOptimization/post-optimization-constants';
import { OptimizationLogsModal } from './OptimizationLogsModal';
import {
  ConstraintListElement,
  StreamingOptimizationPageProps
} from './streaming-optimizations-constants';
import { getOptimizationTitle } from './streaming-optimizations-utils';
import { StreamingOptimizationsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { formatOptimizationDate, getConstraintNameError } from '../../utils/optimization-utils';
import { useSetError } from '../../redux/modals';

const DATE_FORMAT = 'yyyy-MM-dd';
const CUTOFF_DATE = Dfns.format(DATE_FORMAT)(Dfns.startOfISOWeek(new Date()));

const defaultConstraintFormDates = () => ({
  start: Dfns.format(DATE_FORMAT)(Dfns.startOfISOWeek(new Date())),
  end: Dfns.format(DATE_FORMAT)(Dfns.endOfISOWeek(new Date()))
});

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

// Optimization types are stored lowercase; these are the display labels.
const OPTIMIZATION_TYPE_LABELS: Record<string, string> = { buying: 'Buying', traffic: 'Traffic' };

type ConstraintModal = { mode: 'new' } | { mode: 'copy'; source: ConstraintListElement };

export const OptimizationHome = ({ company, streamingType }: StreamingOptimizationPageProps) => {
  const title = getOptimizationTitle(streamingType);
  const navigate = useNavigate();
  const setError = useSetError();
  const [optimizations, setOptimizations] = useState<OptimizationRun[]>();
  const [constraints, setConstraints] = useState<ConstraintListElement[]>();
  const [reload, setReload] = useState(0);
  const [constraintModal, setConstraintModal] = useState<ConstraintModal>();
  const [constraintFormDates, setConstraintFormDates] = useState(defaultConstraintFormDates);
  const [deleteTarget, setDeleteTarget] = useState<ConstraintListElement>();
  const [runTarget, setRunTarget] = useState<ConstraintListElement>();
  const [logsTarget, setLogsTarget] = useState<OptimizationRun>();
  const [optimizationFilterBar, setOptimizationFilterBar] = useState<HTMLDivElement | null>(null);
  const [constraintFilterBar, setConstraintFilterBar] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!company) return undefined;

    let cancelled = false;
    let timer: number | undefined;

    const loadRuns = async () => {
      try {
        const result = await awaitJSON<OptimizationRun[]>(
          await StreamingOptimizationsLambdaFetch('/optimizations', {
            params: { company, streamingType }
          })
        );
        if (cancelled) return;
        setOptimizations(result);
        const hasRunningJobs = result.some(
          row => !row.status.includes('SUCC') && !row.status.includes('FAIL')
        );
        if (hasRunningJobs) {
          timer = window.setTimeout(loadRuns, 30_000);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to fetch optimizations for company "${company}". ${error instanceof Error ? error.message : String(error)}`,
            reportError: error
          });
        }
      }
    };

    const loadConstraints = async () => {
      try {
        const result = await awaitJSON<ConstraintListElement[]>(
          await StreamingOptimizationsLambdaFetch('/list_constraints', {
            params: { company, streamingType }
          })
        );
        if (!cancelled) {
          setConstraints(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to fetch constraints for company "${company}". ${error instanceof Error ? error.message : String(error)}`,
            reportError: error
          });
        }
      }
    };

    (async () => {
      await Promise.all([loadRuns(), loadConstraints()]);
    })();
    return () => {
      cancelled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [company, reload, setError, streamingType]);

  const mutate = async (
    path: string,
    body: Record<string, unknown>,
    message: string
  ): Promise<boolean> => {
    try {
      await StreamingOptimizationsLambdaFetch(path, {
        method: 'POST',
        headers: jsonHeaders,
        body
      });
      setReload(value => value + 1);
      return true;
    } catch (error) {
      await setError({
        message: `${message}. ${error instanceof Error ? error.message : String(error)}`,
        reportError: error
      });
      return false;
    }
  };

  const createConstraint = async ({ name, optimizationType, start, end }) => {
    const now = new Date().toISOString();
    return mutate(
      '/constraints',
      {
        company,
        constraints: {
          avoid: [],
          budget: [],
          creativesAllowed: [],
          end: '',
          global: {
            minExtrapolation: 6,
            unseenMaxSpendPct: 0.25,
            networkMaxSpendPct: 0.2,
            maxExtrapolationRatio: 1.2
          },
          lastmodified: now,
          lastuser: '',
          maximum: [],
          networkCreativesDisallowed: [],
          start,
          total: 100000,
          version: 4
        },
        end,
        lastmodified: now,
        lastuser: '',
        name,
        optimization_type_id: 0,
        optimizationType,
        streamingType,
        start
      },
      `Failed to create constraint "${name}"`
    );
  };

  const copyConstraint = async ({ source, name, start, end }) => {
    try {
      const existing = await awaitJSON<Record<string, unknown>>(
        await StreamingOptimizationsLambdaFetch('/constraints', {
          params: { company, name: source.name, streamingType }
        })
      );
      return await mutate(
        '/constraints',
        {
          ...existing,
          name,
          start,
          end,
          optimizationType: source.optimizationType,
          streamingType
        },
        `Failed to copy constraint "${source.name}"`
      );
    } catch (error) {
      await setError({
        message: `Failed to copy constraint "${source.name}". ${error instanceof Error ? error.message : String(error)}`,
        reportError: error
      });
      return false;
    }
  };

  const optimizationHeaders: TypedHeader<OptimizationRun>[] = [
    {
      label: 'Number',
      name: 'build_number',
      width: 60,
      renderer: row => (
        <Button
          appearance={Appearance.EMPTY}
          size={Size.SM}
          design={row.status.includes('SUCC') ? Design.BRAND : Design.NEUTRAL}
          onClick={() =>
            row.status.includes('SUCC') ? navigate(`runs/${row.build_number}`) : setLogsTarget(row)
          }
          tooltip={{
            text: row.status.includes('SUCC') ? `Open run ${row.build_number}` : 'View run details'
          }}
        >
          {row.build_number}
        </Button>
      )
    },
    {
      label: 'KPI',
      name: 'company_id',
      className: 'optimization-kpi',
      flex: 1,
      minFlexWidth: 175,
      renderer: row => <span title={row.company_id}>{row.company_id}</span>
    },
    {
      label: 'Status',
      name: 'status',
      className: 'optimization-status',
      flex: 1,
      minFlexWidth: 160,
      renderer: row => {
        const status = formatOptimizationStatus(row.status, row.progress);
        return <span title={status}>{status}</span>;
      }
    },
    {
      label: 'Date (Local Time)',
      name: 'date',
      width: 180,
      renderer: row => formatOptimizationDate(row.date)
    },
    { label: 'Constraint Name', name: 'constraint_name', width: 120 },
    { label: 'Branch', name: 'branch', className: 'optimization-branch', width: 60 },
    {
      label: 'OF',
      name: 'objective_value',
      width: 90,
      renderer: row => <NumberFormatter value={row.objective_value} type='#' />
    },
    {
      label: 'Cost',
      name: 'cost',
      width: 100,
      renderer: row => <NumberFormatter value={Number(row.cost)} type='$' />
    },
    {
      label: 'Budget',
      name: 'budget',
      width: 100,
      renderer: row => <NumberFormatter value={Number(row.budget)} type='$' />
    }
  ];

  const constraintHeaders: TypedHeader<ConstraintListElement>[] = [
    {
      label: 'Name',
      name: 'name',
      width: 150,
      renderer: row => (
        <Button
          appearance={Appearance.EMPTY}
          size={Size.SM}
          onClick={() => navigate(`constraints/${encodeURIComponent(row.name)}`)}
        >
          {row.name}
        </Button>
      )
    },
    {
      label: 'Last Modified (Eastern)',
      name: 'lastmodified',
      flex: 1,
      minFlexWidth: 160,
      renderer: row => formatOptimizationDate(row.lastmodified)
    },
    {
      label: '',
      name: 'actions',
      width: 120,
      nonFilterable: true,
      renderer: row => (
        <div className='constraint-actions'>
          <IconButton
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            size={Size.SM}
            aria-label={`Run ${row.name}`}
            tooltip={{ text: 'Run optimization' }}
            onClick={() => setRunTarget(row)}
          >
            <MdPlayArrow />
          </IconButton>
          <IconButton
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            size={Size.SM}
            aria-label={`Copy ${row.name}`}
            tooltip={{ text: 'Copy constraint' }}
            onClick={() => {
              const defaults = defaultConstraintFormDates();
              setConstraintFormDates({
                start: row.start || defaults.start,
                end: row.end || defaults.end
              });
              setConstraintModal({ mode: 'copy', source: row });
            }}
          >
            <MdContentCopy />
          </IconButton>
          <IconButton
            appearance={Appearance.OUTLINED}
            design={Intent.ERROR}
            size={Size.SM}
            aria-label={`Delete ${row.name}`}
            tooltip={{ text: 'Delete constraint' }}
            onClick={() => setDeleteTarget(row)}
          >
            <MdDelete />
          </IconButton>
        </div>
      )
    }
  ];

  const renderOptimizationsLoading = useCallback(
    () =>
      !optimizations ? (
        <Skeleton>
          <TableSkeleton rowHeight={32} headerHeight={32} yOffset={8} />
        </Skeleton>
      ) : (
        <TableEmptyState />
      ),
    [optimizations]
  );

  const renderConstraintsLoading = useCallback(
    () =>
      !constraints ? (
        <Skeleton>
          <TableSkeleton rowHeight={36} headerHeight={32} yOffset={8} />
        </Skeleton>
      ) : (
        <TableEmptyState />
      ),
    [constraints]
  );

  return (
    <Page
      title={title}
      pageType={title}
      minHeight='600px'
      isLoading={!optimizations || !constraints}
    >
      <div className='streaming-optimizations-home'>
        <WidgetContainer
          background={Background.DARK}
          className='optimization-runs-widget'
          header={
            <>
              <h2 className='table-name'>Optimizations</h2>
              <div className='widget-filter-bar-container' ref={setOptimizationFilterBar} />
            </>
          }
        >
          <BPMTable
            autoHeight
            className='optimization-table'
            headerHeight={32}
            rowHeight={32}
            data={optimizations ?? []}
            defaultTokens={{ advanced: ['Status', 'is not like', 'FAILURE'] }}
            defaultAdvancedFilter
            externalFilterBarContainer={optimizationFilterBar}
            headers={optimizationHeaders}
            noRowsRenderer={renderOptimizationsLoading}
          />
        </WidgetContainer>
        <WidgetContainer
          background={Background.DARK}
          className='constraints-widget'
          header={
            <>
              <h2 className='table-name'>Constraints</h2>
              <div className='widget-filter-bar-container' ref={setConstraintFilterBar} />
            </>
          }
          rightActions={
            <IconButton
              appearance={Appearance.OUTLINED}
              size={Size.LG}
              aria-label='New constraint'
              tooltip={{ text: 'New constraint' }}
              onClick={() => {
                setConstraintFormDates(defaultConstraintFormDates());
                setConstraintModal({ mode: 'new' });
              }}
            >
              <MdAdd />
            </IconButton>
          }
        >
          <BPMTable
            autoHeight
            className='constraint-list-table'
            headerHeight={32}
            rowHeight={36}
            data={constraints ?? []}
            externalFilterBarContainer={constraintFilterBar}
            headers={constraintHeaders}
            noRowsRenderer={renderConstraintsLoading}
          />
        </WidgetContainer>
      </div>

      {constraintModal && (
        <ConstraintFormModal
          mode={constraintModal.mode}
          sourceName={constraintModal.mode === 'copy' ? constraintModal.source.name : undefined}
          optimizationTypeOptions={OPTIMIZATION_TYPE_LABELS}
          initialOptimizationType={
            constraintModal.mode === 'copy' ? constraintModal.source.optimizationType : undefined
          }
          disableOptimizationType={constraintModal.mode === 'copy'}
          getNameError={getConstraintNameError}
          isValid={(_name, optimizationType) =>
            !(
              optimizationType === 'traffic' &&
              (!constraintFormDates.start || !constraintFormDates.end)
            )
          }
          renderExtraFields={optimizationType =>
            optimizationType === 'traffic' && (
              <Form.Group>
                <Form.Label>Allocation Date Range</Form.Label>
                <DatePicker
                  appearance={Appearance.OUTLINED}
                  range={
                    constraintFormDates.start && constraintFormDates.end
                      ? { start: constraintFormDates.start, end: constraintFormDates.end }
                      : undefined
                  }
                  background={Background.LIGHT}
                  design={Design.BRAND}
                  isOutsideRange={date => date < CUTOFF_DATE}
                  onChange={({ start, end }) => setConstraintFormDates({ start, end })}
                  removeHeaderText
                />
              </Form.Group>
            )
          }
          onHide={() => setConstraintModal(undefined)}
          onSubmit={async ({ name, optimizationType }) => {
            const input = { name, optimizationType, ...constraintFormDates };
            const succeeded =
              constraintModal.mode === 'copy'
                ? await copyConstraint({ ...input, source: constraintModal.source })
                : await createConstraint(input);
            if (succeeded) {
              setConstraintModal(undefined);
            }
          }}
        />
      )}
      {deleteTarget && (
        <DeleteConstraintModal
          target={deleteTarget}
          onHide={() => setDeleteTarget(undefined)}
          onDelete={async () => {
            const succeeded = await mutate(
              '/delete_constraints',
              { company, name: deleteTarget.name, streamingType },
              `Failed to delete constraint "${deleteTarget.name}"`
            );
            if (succeeded) {
              setDeleteTarget(undefined);
            }
          }}
        />
      )}
      {runTarget && (
        <RunOptimizationModal
          target={runTarget}
          company={company}
          streamingType={streamingType}
          onHide={() => setRunTarget(undefined)}
          onRun={async (branch, kpi) => {
            const succeeded = await mutate(
              '/run_batch_optimizer',
              {
                name: runTarget.name,
                branch,
                kpi,
                company,
                optimizationType: runTarget.optimizationType,
                start: runTarget.start,
                end: runTarget.end,
                streamingType
              },
              `Failed to run constraint "${runTarget.name}"`
            );
            if (succeeded) {
              setRunTarget(undefined);
            }
          }}
        />
      )}
      {logsTarget && (
        <OptimizationLogsModal
          run={logsTarget}
          company={company}
          streamingType={streamingType}
          onHide={() => setLogsTarget(undefined)}
        />
      )}
    </Page>
  );
};

const DeleteConstraintModal = ({ target, onHide, onDelete }) => (
  <Modal show onHide={onHide} centered>
    <Modal.Header closeButton>
      <Modal.Title>Delete Constraint</Modal.Title>
    </Modal.Header>
    <Modal.Body>Delete {target.name}? This cannot be undone.</Modal.Body>
    <Modal.Footer>
      <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
        Cancel
      </Button>
      <Button design={Intent.ERROR} onClick={onDelete}>
        Delete
      </Button>
    </Modal.Footer>
  </Modal>
);

interface RunOptimizationModalProps {
  target: ConstraintListElement;
  company: string;
  streamingType: string;
  onHide: () => void;
  onRun: (branch: string, kpi: string) => Promise<void>;
}

const RunOptimizationModal = ({
  target,
  company,
  streamingType,
  onHide,
  onRun
}: RunOptimizationModalProps) => {
  const setError = useSetError();
  const [branch, setBranch] = useState('v4');
  const [running, setRunning] = useState(false);
  const [optimizationKPIs, setOptimizationKPIs] = useState<string[]>();
  const [optimizationKPI, setOptimizationKPI] = useState<string>();
  const runningRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await awaitJSON<string[]>(
          await StreamingOptimizationsLambdaFetch('/optimization_kpis', {
            params: { company }
          })
        );
        if (!cancelled) {
          setOptimizationKPIs(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to fetch optimization KPIs for company "${company}". ${error.message}`,
            reportError: error
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, streamingType, setError]);

  const run = async () => {
    const kpi = optimizationKPI?.trim();
    if (runningRef.current || !kpi) return;

    runningRef.current = true;
    setRunning(true);
    try {
      await onRun(branch, kpi);
    } finally {
      runningRef.current = false;
      setRunning(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Run Constraint</Modal.Title>
      </Modal.Header>
      <Modal.Body className='optimization-modal-body'>
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control value={target.name} plaintext readOnly tabIndex={-1} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Optimization KPI</Form.Label>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            options={optimizationKPIs ?? []}
            value={optimizationKPI}
            disabled={!optimizationKPIs}
            onChange={setOptimizationKPI}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Branch</Form.Label>
          <Form.Control value={branch} onChange={event => setBranch(event.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Optimization Type</Form.Label>
          <Form.Control
            value={OPTIMIZATION_TYPE_LABELS[target.optimizationType] ?? target.optimizationType}
            plaintext
            readOnly
            tabIndex={-1}
          />
        </Form.Group>
        {target.optimizationType === 'traffic' && (
          <div>
            Allocation: {target.start} – {target.end}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button
          design={Intent.SUCCESS}
          disabled={!branch.trim() || !optimizationKPI?.trim()}
          loading={running}
          onClick={run}
        >
          Run
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
